import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { getReceiptPrintExtras, openReceiptWindow } from "../receiptPrinting";
import "./CheklarTarixi.css";
import { useSklad } from "../SkladContext";

const asArr = (res) => (Array.isArray(res?.data) ? res.data : res?.data?.results || []);
const toNum = (value) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
};
const ymd = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const dateLabel = (value) => {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value || "-";
  return date.toLocaleDateString("uz-UZ", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "long",
  });
};
const timeLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString("uz-UZ", {
    hour: "2-digit",
    minute: "2-digit",
  });
};
const fmtMoney = (value) =>
  `${toNum(value).toLocaleString("uz-UZ", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  })} so'm`;
const payTypeLabel = (value) => {
  const payType = String(value || "").toLowerCase();
  if (payType === "nasiya") return "Nasiya";
  if (payType === "karta") return "Karta";
  if (payType === "terminal") return "Terminal";
  return "Naqd";
};
const lineTotal = (item) => toNum(item?.line_total ?? toNum(item?.qty) * toNum(item?.price));

export default function CheklarTarixi() {
  const today = ymd(new Date());
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const { activeSklad } = useSklad();
  const [warehouse, setWarehouse] = useState(activeSklad);
  useEffect(() => { setWarehouse(activeSklad); }, [activeSklad]);
  const [payType, setPayType] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [warehouses, setWarehouses] = useState([{ id: "all", nom: "Hammasi" }]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(searchText.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const response = await api.get("sklad/");
        if (!active) return;
        const rows = asArr(response);
        setWarehouses([{ id: "all", nom: "Hammasi" }, ...rows]);
      } catch {
        if (active) {
          setWarehouses([{ id: "all", nom: "Hammasi" }]);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const loadReceipts = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");

    try {
      const params = {
        has_items: 1,
        ordering: "-created_at",
      };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (warehouse !== "all") params.sklad = warehouse;
      if (payType !== "all") params.pay_type = payType;
      if (searchQuery) params.search = searchQuery;

      const { data } = await api.get("chek/", { params });
      const rows = Array.isArray(data) ? data : data?.results || [];
      setReceipts(rows);
    } catch (err) {
      console.log("Receipt history error", err?.response?.status, err?.response?.data);
      setReceipts([]);
      setError("Cheklar ro'yxatini yuklashda xatolik yuz berdi.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dateFrom, dateTo, warehouse, payType, searchQuery]);

  useEffect(() => {
    loadReceipts();
  }, [loadReceipts]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadReceipts({ silent: true });
    }, 15000);

    return () => window.clearInterval(timer);
  }, [loadReceipts]);

  const warehouseMap = useMemo(() => {
    const map = new Map();
    warehouses.forEach((item) => {
      map.set(String(item.id), item.nom || item.name || "-");
    });
    return map;
  }, [warehouses]);

  const groupedReceipts = useMemo(() => {
    const groups = new Map();
    receipts.forEach((receipt) => {
      const key = ymd(receipt.created_at) || today;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(receipt);
    });

    return Array.from(groups.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [receipts, today]);

  return (
    <div className="sales-history-page">
      <div className="filters-card">
        <div className="filter-grid">
          <label>
            <span>Qidiruv</span>
            <input
              type="text"
              placeholder="Chek, haridor, mahsulot, seriya..."
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </label>

          <label>
            <span>Dan</span>
            <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>

          <label>
            <span>Gacha</span>
            <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>

          <label>
            <span>Ombor</span>
            <select value={warehouse} onChange={(event) => setWarehouse(event.target.value)}>
              {warehouses.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nom || item.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>To'lov turi</span>
            <select value={payType} onChange={(event) => setPayType(event.target.value)}>
              <option value="all">Hammasi</option>
              <option value="naqd">Naqd</option>
              <option value="nasiya">Nasiya</option>
              <option value="karta">Karta</option>
              <option value="terminal">Terminal</option>
            </select>
          </label>

          <button
            className="btn ghost"
            onClick={() => {
              setDateFrom(today);
              setDateTo(today);
              setWarehouse("all");
              setPayType("all");
              setSearchText("");
              setSearchQuery("");
            }}
            type="button"
          >
            Bugunni ko'rsatish
          </button>
        </div>
      </div>

      {error ? <div className="state error">{error}</div> : null}
      {loading ? <div className="state">Cheklar yuklanmoqda...</div> : null}

      {!loading && !error && groupedReceipts.length === 0 ? (
        <div className="state">Tanlangan filtrlar bo'yicha chek topilmadi.</div>
      ) : null}

      {!loading &&
        !error &&
        groupedReceipts.map(([groupDate, rows]) => {
          const groupTotal = rows.reduce((sum, receipt) => sum + toNum(receipt.total), 0);

          return (
            <section className="date-group" key={groupDate}>
              <div className="date-group-header">
                <div>
                  <h2>{dateLabel(groupDate)}</h2>
                  <p>{rows.length} ta chek</p>
                </div>
                <strong>{fmtMoney(groupTotal)}</strong>
              </div>

              <div className="receipt-list">
                {rows.map((receipt) => {
                  const items = Array.isArray(receipt.items) ? receipt.items : [];
                  const totalQty = items.reduce((sum, item) => sum + toNum(item.qty), 0);

                  return (
                    <article className="receipt-card" key={receipt.id}>
                      <div className="receipt-head">
                        <div>
                          <div className="receipt-id">Chek #{receipt.check_no || receipt.id}</div>
                          <h3>{receipt.buyer_name || "Noma'lum haridor"}</h3>
                          <p>
                            {timeLabel(receipt.created_at)}
                            {" | "}
                            {receipt.created_by_username || "Operator ko'rsatilmagan"}
                          </p>
                        </div>

                        <div className="receipt-actions">
                          <button
                            className="btn primary"
                            onClick={() => openReceiptWindow(receipt, getReceiptPrintExtras(receipt))}
                            type="button"
                          >
                            Chek chiqarish
                          </button>
                          <Link className="btn ghost" to={`/chek/${receipt.id}`}>
                            Alohida sahifa
                          </Link>
                        </div>
                      </div>

                      <div className="receipt-badges">
                        <span>{payTypeLabel(receipt.pay_type)}</span>
                        <span>{warehouseMap.get(String(receipt.sklad)) || "Ombor aniqlanmagan"}</span>
                        <span>{totalQty.toLocaleString("uz-UZ", { maximumFractionDigits: 3 })} dona</span>
                        <span>{fmtMoney(receipt.total)}</span>
                      </div>

                      {receipt.note ? <div className="receipt-note">{receipt.note}</div> : null}

                      <div className="items-table">
                        <div className="items-head">
                          <span>Mahsulot</span>
                          <span>Soni</span>
                          <span>Narx</span>
                          <span>Jami</span>
                        </div>
                        {items.map((item) => (
                          <div className="items-row" key={item.id || `${receipt.id}-${item.product_id}-${item.series}`}>
                            <div>
                              <strong>{item.product_name || "Noma'lum"}</strong>
                              <small>{item.series || "-"}</small>
                            </div>
                            <span>{toNum(item.qty).toLocaleString("uz-UZ", { maximumFractionDigits: 3 })}</span>
                            <span>{fmtMoney(item.price)}</span>
                            <span>{fmtMoney(lineTotal(item))}</span>
                          </div>
                        ))}
                      </div>

                      <div className="receipt-foot">
                        <div>
                          <span>Jami</span>
                          <strong>{fmtMoney(receipt.subtotal)}</strong>
                        </div>
                        <div>
                          <span>Chegirma</span>
                          <strong>{fmtMoney(receipt.discount)}</strong>
                        </div>
                        <div>
                          <span>Yakuniy</span>
                          <strong>{fmtMoney(receipt.total)}</strong>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })}
    </div>
  );
}
